import { ModuleRegistry } from 'ag-grid-community';
import type { DefaultMenuItem, GetMainMenuItemsParams, GridApi, GridOptions } from 'ag-grid-community';
import { ColumnMenuModule } from 'ag-grid-enterprise';
import { Array as A, pipe } from 'effect';

import { isServiceColumn } from '@/components/AgGrid/helpers';

// The one column-menu setup for every non-Pay grid (DA-20225 new column menu, design Decisions 1–3).
// Grids get it through BASE_GRID_OPTIONS (@/components/AgGrid/helpers/baseGridOptions) and set no columnMenu /
// getMainMenuItems / mainMenuItems / columnMenuItems / menuTabs / suppressMenuHide of their own.
// Custom filters keep their own Apply/Reset under the new menu: the grid adds its own buttons only under
// `enableFilterHandlers`, a separate grid option.

// Registered here, not per grid: registration is app-wide, and a grid that relied on another page's list
// had a pin button that opened nothing until that page had been visited.
ModuleRegistry.registerModules([ColumnMenuModule]);

const PIN_MENU_ITEMS: ReadonlyArray<DefaultMenuItem> = ['pinSubMenu', 'separator', 'autoSizeThis', 'autoSizeAll'];

const dropStraySeparators = (items: ReadonlyArray<DefaultMenuItem>): Array<DefaultMenuItem> =>
  pipe(
    items,
    A.filter(
      (item, index) =>
        item !== 'separator' || (index > 0 && index < items.length - 1 && items[index - 1] !== 'separator'),
    ),
  );

// Pin Column, then Autosize This / All — what the legacy general tab showed. A whitelist, so items a later
// AG Grid adds stay out. An item AG Grid does not offer for this column (defaultItems) is not forced in.
export const getPinMenuItems = ({
  column,
  defaultItems,
}: Pick<GetMainMenuItemsParams, 'column' | 'defaultItems'>): Array<DefaultMenuItem> => {
  if (!column) return [];

  const isService = isServiceColumn(column.getColId());

  return pipe(
    PIN_MENU_ITEMS,
    A.filter((item) => item === 'separator' || defaultItems.includes(item)),
    A.filter((item) => !(isService && item === 'autoSizeThis')),
    dropStraySeparators,
  );
};

// Grids whose column menu is being opened by the header's pin button right now. AG Grid builds the menu
// items synchronously inside showColumnMenu, so the mark never outlives the call.
const openedFromHeader = new WeakSet<GridApi>();

// Every other way to open the column menu — right-click on a column, a group or empty header space, a long
// tap, the keyboard — gets an empty list, and AG Grid opens nothing (design Decision 3). A per-column
// suppressHeaderContextMenu cannot do this: it does not reach empty header space.
export const openHeaderColumnMenu = (api: GridApi, open: () => void): void => {
  openedFromHeader.add(api);
  try {
    open();
  } finally {
    openedFromHeader.delete(api);
  }
};

const getMainMenuItems = (params: GetMainMenuItemsParams): Array<DefaultMenuItem> =>
  openedFromHeader.has(params.api) ? getPinMenuItems(params) : [];

export const COLUMN_MENU_GRID_OPTIONS = {
  columnMenu: 'new',
  getMainMenuItems,
} as const satisfies GridOptions;
